import { useCallback, useRef, useState, ChangeEvent, RefObject } from "react";
import styles from "./AttachFiles.module.css";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import PaperclipIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PaperclipIconPath";
import { useTranslation } from "@coworker/locales";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import TrashCanIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/TrashCanIconPath";
import { FixaToast } from "@coworker/apprestructured/src/shared/wrappers/FixaToast/FixaToast";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import ListItem from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ListItem";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import StyledModalFooter from "../StyledModalFooter/StyledModalFooter";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import {
  createNewOrExistingFileKey,
  fileToNewFile,
  NewOrExistingFile,
} from "@coworker/apprestructured/src/shared/types/newOrExistingFile";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_FILE_TYPES =
  ".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

type FileInputProps = {
  inputRef: RefObject<HTMLInputElement | null>;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
};

const FileInput = ({ inputRef, onChange, disabled }: FileInputProps) => (
  <input
    className={styles["hiddenInput"]}
    type="file"
    ref={inputRef}
    multiple
    onChange={onChange}
    accept={ACCEPTED_FILE_TYPES}
    disabled={disabled}
  />
);

type AttachFilesProps = {
  disabled: boolean;
  files: NewOrExistingFile[];
  onFilesChanged: (newOrExistingFiles: NewOrExistingFile[]) => void;
};

function AttachFiles({ disabled, files, onFilesChanged }: AttachFilesProps) {
  const [showModal, setShowModal] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastText, setToastText] = useState("");
  const [modalNewOrExistingFiles, setModalNewOrExistingFiles] =
    useState<NewOrExistingFile[]>(files);
  const [hasTriedAddFiles, setHasTriedAddFiles] = useState(false);

  const rootInputRef = useRef<HTMLInputElement>(null);
  const modalInputRef = useRef<HTMLInputElement>(null);

  const closeAndResetModal = useCallback(() => {
    setShowModal(false);
    setToastOpen(false);
    setToastText("");
    setHasTriedAddFiles(false);
    setModalNewOrExistingFiles(files);
  }, [files]);

  const {
    saveGuardedFunction: handleCloseClick,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(closeAndResetModal);

  const { t } = useTranslation();

  const hasUploadedFiles = files.length > 0;
  const hasAddedFiles = modalNewOrExistingFiles.length > 0;

  const showNoFilesSelected =
    modalNewOrExistingFiles.length === 0 &&
    (hasTriedAddFiles || hasUploadedFiles);

  const handleFilesChange = useCallback(
    (files: NewOrExistingFile[]) => {
      setModalNewOrExistingFiles(files);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleAttachMoreClick = useCallback((inModal = false) => {
    (inModal ? modalInputRef.current : rootInputRef.current)?.click();
  }, []);

  const handleListViewItemClick = useCallback(() => {
    if (hasUploadedFiles || hasAddedFiles) {
      setShowModal(true);
      setModalNewOrExistingFiles(files);
      setHasTriedAddFiles(false);
    } else {
      handleAttachMoreClick(false);
    }
  }, [files, hasUploadedFiles, hasAddedFiles, handleAttachMoreClick]);

  const handleRemoveFile = useCallback(
    (file: NewOrExistingFile) => {
      handleFilesChange(modalNewOrExistingFiles.filter((f) => f !== file));
    },
    [modalNewOrExistingFiles, handleFilesChange]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setHasTriedAddFiles(true);
      const selectedFiles = Array.from(e.target.files || []);

      const uniqueNewFiles = selectedFiles.filter(
        (selectedFile) =>
          !modalNewOrExistingFiles.some(
            (modalFile) =>
              modalFile.file.name === selectedFile.name &&
              modalFile.file.size === selectedFile.size
          )
      );

      const tooLargeFiles = uniqueNewFiles.filter(
        (f) => f.size > MAX_FILE_SIZE
      );
      const validFiles = uniqueNewFiles.filter((f) => f.size <= MAX_FILE_SIZE);

      if (tooLargeFiles.length > 0) {
        setToastText(
          t("tooLargeFilesErrorString", {
            maxFileSize: `${MAX_FILE_SIZE / (1024 * 1024)} MB`,
          })
        );
        setToastOpen(true);
      }

      if (validFiles.length > 0) {
        handleFilesChange([
          ...modalNewOrExistingFiles,
          ...validFiles.map(fileToNewFile),
        ]);
        if (!showModal) setShowModal(true);
      }

      e.target.value = "";
    },
    [
      modalNewOrExistingFiles,
      setHasTriedAddFiles,
      setToastText,
      setToastOpen,
      t,
      handleFilesChange,
      showModal,
      setShowModal,
    ]
  );

  const handleConfirmClick = useCallback(() => {
    onFilesChanged(modalNewOrExistingFiles);
    closeAndResetModal();
    disableSaveGuard();
  }, [
    onFilesChanged,
    modalNewOrExistingFiles,
    closeAndResetModal,
    disableSaveGuard,
  ]);

  return (
    <>
      <ListItem
        title={t(
          hasUploadedFiles ? "filesStringCapitalized" : "attachFilesString"
        )}
        description={
          hasUploadedFiles
            ? files.length === 1
              ? `1 ${t("fileString")}`
              : `${files.length} ${t("filesString")}`
            : "PDF, XLS, Word"
        }
        icon={PaperclipIconPath}
        onClick={handleListViewItemClick}
        disabled={disabled}
        emphasised={hasUploadedFiles}
      />
      <FileInput
        inputRef={rootInputRef}
        onChange={handleChange}
        disabled={disabled}
      />
      <FixaModal visible={showModal} handleCloseBtn={handleCloseClick}>
        <FixaSheets
          header={
            <FixaModalHeader
              closeBtnClick={handleCloseClick}
              title={t("attachFilesString")}
            />
          }
          footer={
            <StyledModalFooter>
              <FixaButton
                ssrIcon={PaperclipIconPath}
                iconPosition="trailing"
                text={t(
                  showNoFilesSelected
                    ? "attachFilesString"
                    : "attachMoreFilesString"
                )}
                type="secondary"
                onClick={() => handleAttachMoreClick(true)}
                fluid
              />
              <FixaButton
                text={t("confirmString")}
                type="primary"
                onClick={handleConfirmClick}
                fluid
              />
            </StyledModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            {hasAddedFiles && (
              <FixaListView id="attachedFilesList">
                {modalNewOrExistingFiles.map((newOrExistingFile) => (
                  <FixaListViewItem
                    key={createNewOrExistingFileKey(newOrExistingFile)}
                    title={newOrExistingFile.file.name}
                    controlIcon={null}
                    control="default"
                    quantityLabel={
                      <FixaButton
                        onClick={() => handleRemoveFile(newOrExistingFile)}
                        iconOnly
                        ssrIcon={TrashCanIconPath}
                        size="small"
                      />
                    }
                  />
                ))}
              </FixaListView>
            )}
            {showNoFilesSelected && (
              <FixaText textStyle="Body.L">
                {t("noFilesSelectedString")}
              </FixaText>
            )}
            <FileInput
              inputRef={modalInputRef}
              onChange={handleChange}
              disabled={disabled}
            />
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
      <FixaToast
        isOpen={toastOpen}
        onCloseRequest={() => setToastOpen(false)}
        text={toastText}
      />
    </>
  );
}

export default AttachFiles;
